import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import * as ClassicEditor from '../../../../assets/js/ckeditor';
import { Categorie, Template } from '@models/email/template';
import { AtlasToastService } from '@services/atlas-toast.service';
import { EmailService } from '@services/email.service';
import { LoaderService } from '@services/loader.service';
import { finalize } from 'rxjs/operators';
@Component({
  selector: 'wsb-template',
  templateUrl: './template.component.html',
  styleUrls: ['./template.component.scss'],
  standalone: false
})
export class TemplateComponent implements OnInit {
  @Input() page: string;
  @Output() templateSelected = new EventEmitter<any>();
  currentType = 'ADVANCED';
  categories: Array<Categorie> = [];
  templates: Array<Template>;
  categorySelected = 'ALL';
  templateDeleteHtml;
  faPreviewIcon = faSearch;
  loadingTemplates = false;
  public EditorE = ClassicEditor;
  htmlEditorForm: UntypedFormGroup = null;
  importFileForm: UntypedFormGroup = null;
  htmlEditorUseText: boolean;
  @ViewChild('htmlEditorModal') htmlEditorModal;
  @ViewChild('importFileModal') importFileModal;
  @ViewChild('successImportModal') successImportModal;
  modal: NgbModalRef;
  ckeConfig: any;
  loading = false;
  templateDeleteRef;
  constructor(
    private emailService: EmailService,
    private modalService: NgbModal,
    private formBuilder: UntypedFormBuilder,
    private loaderService: LoaderService,
    private atlasToast: AtlasToastService
  ) {}

  ngOnInit(): void {
    this.getTemplates(this.currentType);
    this.getCategories();
    this.htmlEditorForm = this.formBuilder.group({
      name: ['', Validators.required],
      bodyHtml: ['', Validators.required],
      bodyText: [''],
      inlineHtml: ['']
    });
    this.importFileForm = this.formBuilder.group({
      file: ['', Validators.required],
      name: ['', Validators.required]
    });
  }

  getTemplates(type, category?) {
    this.loaderService.show();
    this.loadingTemplates = true;
    this.templates = [];
    this.emailService
      .getTemplates(type, category)
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe(data => {
        this.templates = data.data;
        this.loadingTemplates = false;
      });
  }

  getCategories() {
    const allCategories: Array<Categorie> = [];
    const defaultCategory = Categorie.deserialize({
      idCategory: 'ALL',
      shortDescription: 'mail_templates_advanced_category_all',
      numTemplates: 0,
      isBasic: false
    });
    allCategories.push(defaultCategory);
    this.emailService.getCategories().subscribe(data => {
      allCategories.push(...data.data);
      this.categories = allCategories;
    });
  }

  changeType(type) {
    if (type !== 'IMPORT') {
      this.categorySelected = 'ALL';
      this.getTemplates(type);
    }
  }

  selectCategory(category) {
    if (category !== 'ALL') {
      this.getTemplates(this.currentType, category);
    } else {
      this.getTemplates(this.currentType);
    }
  }

  openDeleteModal(dialog: TemplateRef<any>, template) {
    this.templateDeleteRef = template;
    this.modal = this.modalService.open(dialog);
  }

  deleteTemplateRef(): void {
    this.emailService.deleteTemplate(this.templateDeleteRef).subscribe(
      () => {
        this.modal.close();
        this.atlasToast.success('common_success', null, 6000);
        this.selectCategory(this.categorySelected);
      },
      () => {
        this.atlasToast.error('common_error', null);
      },
      () => {}
    );
  }

  openHtmlEditor() {
    this.modal = this.modalService.open(this.htmlEditorModal, { size: 'lg' });
  }

  openFileUpload() {
    this.modal = this.modalService.open(this.importFileModal, { size: 'lg' });
  }

  useTemplate(template) {
    window.location.href = `/mail/send?template=${template.idTemplate}&new_version=true`;
    // window.location.href = `/mail-create-test?template=${template.idTemplate}`;
  }

  saveHtmlTemplate() {
    this.htmlEditorForm.get('name').updateValueAndValidity();
    this.htmlEditorForm.get('bodyHtml').updateValueAndValidity();
    this.loading = true;
    if (this.htmlEditorForm.valid) {
      this.emailService.createTemplateHtml(this.htmlEditorForm.getRawValue()).subscribe(
        () => {
          this.htmlEditorForm.reset();
          this.modal.close();
          this.atlasToast.success('common_success', null, 6000);
          this.loading = false;
        },
        () => {
          this.atlasToast.error('common_error', null);
          this.loading = false;
        },
        () => {}
      );
    }
  }

  uploadFile() {
    this.importFileForm.get('name').updateValueAndValidity();
    this.importFileForm.get('file').updateValueAndValidity();
    this.loading = true;
    if (this.importFileForm.valid) {
      this.emailService.createTemplateFile(this.importFileForm.getRawValue()).subscribe(
        () => {
          this.importFileForm.reset();
          this.modal.close();
          this.atlasToast.success('common_success', null, 6000);
          this.loading = false;
        },
        () => {
          this.atlasToast.error('common_error', null);
          this.loading = false;
        },
        () => {}
      );
    }
  }

  onFileUploadChange(event) {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      this.importFileForm.get('file').setValue(file);
      this.importFileForm.markAsDirty();
    } else {
      this.importFileForm.get('file').setValue(null);
    }
  }

  onFileSelect(event) {
    if (event.length > 0) {
      const file = event[0];
      this.importFileForm.get('file').setValue(file);
    }
  }

  useTemplateToCreateEmailCampaign(template) {
    this.templateSelected.emit(template);
  }
}
